import dayjs from 'dayjs'
import _ from 'lodash'
import { isAlive, isStateTreeNode } from 'mobx-state-tree'

import {
  accountTypeKeyToPlanTypeId,
  BOND_CASH_OTHER_SECURITIES,
  getSecurityOptionsList,
  SECURITY_TYPES,
  VALUATION_METHODS,
} from '../../../constants'
import { trackInvestmentsByTaxType } from '../utils'
import { getIraTaxType } from '../utils/accountTypeToAllowedContributions'

export const initializeValues = ({
  accountType,
  showAddBalanceButton,
  showPreTaxForm,
  showRothForm,
  showAfterTaxForm,
  notInvestedAlreadyExistsForSecurity,
}) => {
  // Even when the shares control is available, we default to entering a total balance
  const preTax = {
    quantity: undefined,
    purchaseDate: dayjs().format('YYYY-MM-DD'),
    totalCostBasis: 0,
    totalValue: undefined,
    valuationMethod: VALUATION_METHODS.accountBalance,
  }

  const roth = {
    quantity: undefined,
    purchaseDate: dayjs().format('YYYY-MM-DD'),
    totalCostBasis: 0,
    totalValue: undefined,
    valuationMethod: VALUATION_METHODS.accountBalance,
  }

  const afterTax = {
    quantity: undefined,
    purchaseDate: dayjs().format('YYYY-MM-DD'),
    totalCostBasis: undefined,
    totalValue: undefined,
    valuationMethod: VALUATION_METHODS.accountBalance,
  }

  // If account is an ira
  if (
    _.includes(['ira-non-deductible', 'ira-deductible', 'ira-rollover', 'ira-roth'], accountType)
  ) {
    const taxTypes = { preTax, roth, afterTax }
    const values = {
      addBalance: true,
      showAddBalanceButton,
      preTax: null,
      roth: null,
      afterTax: null,
    }
    const taxType = getIraTaxType(accountType)
    values[taxType] = taxTypes[taxType]
    return values
  }
  if (
    !notInvestedAlreadyExistsForSecurity &&
    trackInvestmentsByTaxType(accountType) &&
    showPreTaxForm &&
    (showRothForm || showAfterTaxForm)
  ) {
    // If more than one form available for NonGC case (roth or after-tax is allowed), ask to add each individually
    return {
      addBalance: showAddBalanceButton ? null : true,
      showAddBalanceButton,
      preTax: showAddBalanceButton === false && showPreTaxForm ? preTax : null,
      roth: showAddBalanceButton === false && showRothForm ? roth : null,
      afterTax: showAddBalanceButton === false && showAfterTaxForm ? roth : null,
    }
  }

  // With only one tax type on offer there is no choice to put to the user, so its balance opens up
  // front and the form leaves its "Add to <tax type> balance?" question out. Seeding it here is
  // what lets the form drop the question outright rather than hiding it once answered.
  if (
    trackInvestmentsByTaxType(accountType) &&
    [showPreTaxForm, showRothForm, showAfterTaxForm].filter(Boolean).length === 1
  ) {
    return {
      addBalance: true,
      showAddBalanceButton,
      preTax: showPreTaxForm ? preTax : null,
      roth: showRothForm ? roth : null,
      afterTax: showAfterTaxForm ? afterTax : null,
    }
  }

  // If any form is available
  if (
    !notInvestedAlreadyExistsForSecurity &&
    trackInvestmentsByTaxType(accountType) &&
    (showPreTaxForm || showRothForm || showAfterTaxForm)
  ) {
    return {
      addBalance: showAddBalanceButton ? null : true,
      showAddBalanceButton,
      preTax: null,
      roth: null,
      afterTax: null,
    }
  }

  // If only single form for afterTax only account types, assume both Add Balance and add to afterTax is true
  if (!trackInvestmentsByTaxType(accountType)) {
    return { addBalance: true, showAddBalanceButton, preTax: null, roth: null, afterTax }
  }

  // If Not Invested already exists for this security, assume Add Balance and ask them to add tax types
  if (notInvestedAlreadyExistsForSecurity) {
    return {
      addBalance: true,
      showAddBalanceButton: false,
      preTax: null,
      roth: null,
      afterTax: null,
    }
  }

  // The following case should only run when investments already added and available for edit
  if (!showPreTaxForm && !showRothForm && !showAfterTaxForm) {
    return {
      addBalance: showAddBalanceButton ? null : true,
      showAddBalanceButton,
      preTax: null,
      roth: null,
      afterTax: null,
    }
  }
}

/**
 * Drops investments whose nodes have left the state tree.
 *
 * Reloading an account replaces its investments wholesale, destroying the nodes any mounted
 * component was handed. Those components observe their nodes, so their own reactions can re-run
 * against the destroyed ones before the list above them re-renders — reading a dead node warns
 * once per read, which adds up fast over a list. Reading `investment.value` is enough to trigger
 * it, since that view reaches for valuationMethod, totalValue and quantity.
 *
 * Anything that is not a node passes through, both because `isAlive` throws on non-nodes and so
 * tests can pass plain objects.
 *
 * @param {Array} investments
 * @returns {Array}
 */
export const aliveInvestments = investments =>
  (investments ?? []).filter(investment => !isStateTreeNode(investment) || isAlive(investment))

export const typeToInvestmentsAPI = ({ type, id }) =>
  ({
    'institutional': 'accounts/institutionalAccount/investments',
    'non-gc': `accounts/nonGCAccount/${id}/investments`,
  })[type]

/**
 * The security types the name/ticker search covers. Bond, Cash and Other securities are internal
 * proxies rather than tradeable securities, so they are only ever offered through the generic
 * asset class list.
 */
export const SEARCHABLE_SECURITY_TYPES = [SECURITY_TYPES.Stock, SECURITY_TYPES['Mutual Fund']]

/** Generic asset class groups, in the order they are shown in the search pop-up. */
const GENERIC_ASSET_CLASS_GROUPS = [
  { label: 'Bonds', securityType: SECURITY_TYPES.Bond },
  { label: 'Cash', securityType: SECURITY_TYPES.Cash },
  { label: 'Other', securityType: SECURITY_TYPES.Other },
]

/**
 * Builds the grouped generic asset class list for an account.
 *
 * The three internal lists overlap - the Other list repeats entries that already appear under
 * Bonds or Cash - so anything already offered by an earlier group is dropped from the later ones.
 * Securities are matched both by id and by name, since the lists carry a few near-identical
 * entries under different ids.
 *
 * @param {string} accountType account type key from the route, e.g. '401k'
 * @returns {Array<{label: string, securities: Array<{id: number, securityName: string}>}>}
 */
export const getGenericAssetClasses = accountType => {
  const planTypeId = accountTypeKeyToPlanTypeId[accountType]
  const alreadyOffered = new Set()

  return GENERIC_ASSET_CLASS_GROUPS.map(({ label, securityType }) => {
    const securities = []

    getSecurityOptionsList(planTypeId, securityType).forEach(
      ({ label: securityName, value: id }) => {
        const nameKey = _.toLower(_.trim(securityName))

        if (alreadyOffered.has(id) || alreadyOffered.has(nameKey)) {
          return
        }

        alreadyOffered.add(id)
        alreadyOffered.add(nameKey)
        securities.push({ id, securityName })
      }
    )

    return { label, securities }
  }).filter(({ securities }) => securities.length > 0)
}

/**
 * True when the security is one of our internal generic proxies, whose tickers are internal codes
 * that mean nothing to the user and so are never shown.
 *
 * @param {number} securityId
 * @returns {boolean}
 */
export const isGenericSecurity = securityId =>
  _.some(BOND_CASH_OTHER_SECURITIES, security => security.id === securityId)

/**
 * Orders search results by how well they match what was typed: ticker matches beat name matches,
 * and matches at the start of a ticker, name or word beat matches buried in the middle. Ties keep
 * alphabetical order by name.
 *
 * @param {Array} securities
 * @param {string} query
 * @returns {Array}
 */
export const rankSecurities = (securities, query) => {
  const search = _.toLower(_.trim(query))

  const relevance = ({ ticker, securityName }) => {
    const tickerText = _.toLower(ticker || '')
    const nameText = _.toLower(securityName || '')

    if (tickerText === search) return 0
    if (_.startsWith(tickerText, search)) return 1
    if (_.startsWith(nameText, search)) return 2
    if (_.some(nameText.split(/[^a-z0-9]+/), word => _.startsWith(word, search))) return 3
    if (_.includes(nameText, search)) return 4
    if (_.includes(tickerText, search)) return 5
    return 6
  }

  return _.sortBy(securities, [relevance, ({ securityName }) => _.toLower(securityName || '')])
}

/**
 * Whether there is anything to submit.
 *
 * Each tax bucket has its own "Add to <tax type> balance?" toggle, so the user can answer No to
 * every one of them while still having chosen Add Balance. That leaves nothing for handleSave to
 * post, which would otherwise look like a successful save, so the save buttons stay disabled until
 * the investment is either marked not invested or carries at least one balance.
 *
 * @param {Object} values current form values
 * @returns {boolean}
 */
export const canSaveInvestment = ({ addBalance, preTax, roth, afterTax } = {}) =>
  addBalance === false || Boolean(preTax || roth || afterTax)

/**
 * The tax buckets to restore when the Value question is flipped.
 *
 * Choosing Not Invested clears every balance. Choosing Add Balance again puts back whatever the
 * form opened with, which matters when only one tax type is on offer: its own question is not on
 * screen to reopen the balance, so without this the form is left blank and unsaveable.
 *
 * @param {boolean} addBalance the answer to the Value question
 * @param {Object} initialValues the values the form opened with
 * @returns {{preTax: Object|null, roth: Object|null, afterTax: Object|null}}
 */
export const balancesForValueChoice = (addBalance, initialValues = {}) =>
  addBalance
    ? {
        preTax: initialValues.preTax ?? null,
        roth: initialValues.roth ?? null,
        afterTax: initialValues.afterTax ?? null,
      }
    : { preTax: null, roth: null, afterTax: null }
